/**
 * Agent Mode Overlay
 * 
 * Immersive voice + text interface with 2-column layout
 * Features:
 * - Left: Voice visualization and controls
 * - Right: Chat panel with messages, transcription, and text input
 * - Schwarz-lila gradient design
 * - MCP-powered AI agent (using Gemini)
 * - Mobile-optimized (stacked layout)
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Mic,
  MicOff,
  X,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Zap,
  Brain,
  Send,
  MessageSquare,
  User,
  Bot,
  Info
} from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import { useAuth } from '../../hooks/useAuth';
import { GeminiLiveAgent } from '../../services/geminiLiveAgent';
import { VoiceVisualizer } from './VoiceVisualizer';
import { getMCPServer } from '../../mcp';
import { unifiedAgentService, SUGGESTED_QUESTIONS, PROACTIVE_EVENTS } from '../../services/unifiedAgentService';
import { EchobotContext, EchobotContextData } from '../../types/echobot';

interface AgentModeOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: EchobotContext;
  contextData?: EchobotContextData;
}

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  toolCalls?: string[];
}

export const AgentModeOverlay: React.FC<AgentModeOverlayProps> = ({ 
  isOpen, 
  onClose, 
  currentPage = 'dashboard',
  contextData 
}) => {
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [agentStatus, setAgentStatus] = useState<'idle' | 'listening' | 'thinking' | 'speaking'>('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [textInput, setTextInput] = useState('');
  const [proactiveMessage, setProactiveMessage] = useState<string | null>(null);
  const [unifiedServiceReady, setUnifiedServiceReady] = useState(false);
  
  const voiceAgentRef = useRef<GeminiLiveAgent | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (isOpen) {
      initializeAgent();
      initializeUnifiedService();
    } else {
      cleanupAgent();
    }

    return () => {
      cleanupAgent();
    };
  }, [isOpen]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentTranscript]);

  // Initialize unified agent service for context awareness
  const initializeUnifiedService = async () => {
    try {
      const orgId = user?.profile?.orgId || user?.uid || '';
      const userId = user?.uid || '';
      
      await unifiedAgentService.initialize({
        orgId,
        userId,
        language: 'de',
        context: contextData
      });
      
      setUnifiedServiceReady(true);
      
      // Check for proactive events
      if (contextData) {
        const proactive = unifiedAgentService.detectProactiveEvent(contextData);
        if (proactive) {
          setProactiveMessage(proactive);
          // Show proactive message after a delay
          setTimeout(() => {
            addMessage({
              type: 'system',
              content: `💡 ${proactive}`
            });
          }, 2000);
        }
      }
      
      console.log('✅ Unified Agent Service ready for Agent Mode');
    } catch (error) {
      console.error('Error initializing unified service:', error);
    }
  };

  const initializeAgent = async () => {
    try {
      console.log('🤖 Initializing Agent Mode with Gemini...');
      setError(null);
      setMessages([]); // Clear previous messages
      
      // Step 1: Validate Gemini API Key
      addMessage({
        type: 'system',
        content: '🔑 Prüfe Gemini API Key...'
      });
      
      const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
      if (!apiKey) {
        const errorMsg = 'Gemini API Key fehlt!';
        setError(errorMsg);
        addMessage({
          type: 'system',
          content: '❌ ' + errorMsg
        });
        return;
      }
      
      addMessage({
        type: 'system',
        content: '✅ Gemini API Key gefunden'
      });
      
      // Step 2: Initialize MCP Server
      addMessage({
        type: 'system',
        content: '🔧 Initialisiere MCP Server...'
      });
      
      const mcpServer = getMCPServer();
      try {
        await mcpServer.start();
        console.log('✅ MCP Server started/verified');
      } catch (err) {
        console.warn('MCP Server start warning (may already be running):', err);
      }
      
      // Check MCP Server health
      const health = await mcpServer.getHealthStatus();
      console.log('📊 MCP Server health:', health);
      
      if (health.status !== 'healthy') {
        addMessage({
          type: 'system',
          content: '⚠️ MCP Server nicht optimal - CRM-Aktionen evtl. eingeschränkt'
        });
      } else {
        addMessage({
          type: 'system',
          content: '✅ MCP Server bereit'
        });
      }

      // Get orgId - prioritize profile.orgId for multi-org support
      const orgId = user?.profile?.orgId || user?.uid || '';
      const userId = user?.uid || '';
      
      console.log('🔍 Agent Context:', { userId, orgId });

      // Step 3: Initialize Gemini Live Agent
      addMessage({
        type: 'system',
        content: '🎤 Initialisiere Gemini Live Agent...'
      });
      
      voiceAgentRef.current = new GeminiLiveAgent({
        orgId,
        userId,
        mcpServer,
        language: 'de',
        voice: 'Kore' // German-friendly voice
      });

      try {
        await voiceAgentRef.current.initialize();
        addMessage({
          type: 'system',
          content: '✅ Gemini Live Agent bereit!'
        });
      } catch (initError: any) {
        console.error('Voice Agent init error:', initError);
        setError(initError.message);
        addMessage({
          type: 'system',
          content: '❌ ' + initError.message
        });
        return;
      }

      // Setup callbacks
      voiceAgentRef.current.onResponse((response) => {
        if (response.text) {
          addMessage({
            type: 'assistant',
            content: response.text,
            toolCalls: response.toolCalls?.map(tc => tc.name)
          });
        }
        setIsProcessing(false);
        setIsSpeaking(false);
        setAgentStatus('idle');
      });

      voiceAgentRef.current.onToolCall((toolName, args) => {
        console.log(`🔧 Tool called: ${toolName}`, args);
        addMessage({
          type: 'system',
          content: `🔧 ${toolName}...`
        });
      });

      voiceAgentRef.current.onTranscript((transcript, isFinal) => {
        setCurrentTranscript(transcript);
        if (isFinal && transcript) {
          addMessage({
            type: 'user',
            content: transcript
          });
        }
      });

      voiceAgentRef.current.onStatus((status) => {
        console.log('📊 Status:', status);
      });

      // Final success message
      console.log('✅ Agent Mode initialized with Gemini Live API');
      addMessage({
        type: 'system',
        content: '🎉 Agent Mode bereit!\n\nKlicke "Konversation Starten" und sprich mit mir!\n\nGoogle Gemini Live API mit hochwertiger Stimme (Kore).\n\nBeispiele:\n• "Hallo!"\n• "Zeige mir meine Aufgaben"\n• "Briefing für Microsoft"'
      });
      
    } catch (error: any) {
      console.error('❌ Error initializing Agent Mode:', error);
      setError(error.message);
      
      addMessage({
        type: 'system',
        content: `❌ Fehler beim Initialisieren: ${error.message}\n\nBitte prüfe die Browser-Konsole für Details.`
      });
    }
  };

  const setupAudioAnalyzer = () => {
    try {
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      
      // Start monitoring audio levels
      monitorAudioLevel();
    } catch (error) {
      console.error('Error setting up audio analyzer:', error);
    }
  };

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average amplitude
      const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
      setAudioLevel(average / 255); // Normalize to 0-1

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  };

  const cleanupAgent = () => {
    if (voiceAgentRef.current) {
      voiceAgentRef.current.disconnect();
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const handleStartConversation = async () => {
    if (!voiceAgentRef.current) return;

    try {
      console.log('🗣️ Starting Gemini Live conversation...');
      setIsListening(true);
      setAgentStatus('listening');
      setError(null);
      setCurrentTranscript('');
      
      addMessage({
        type: 'system',
        content: '🔌 Verbinde mit Gemini Live API...'
      });

      // Start listening - connects to Gemini Live and starts microphone
      await voiceAgentRef.current.startListening();
      
      addMessage({
        type: 'system',
        content: '🎤 Konversation aktiv - sprich einfach natürlich!'
      });

    } catch (error: any) {
      console.error('Error starting conversation:', error);
      setError(error.message);
      addMessage({
        type: 'system',
        content: '❌ Fehler: ' + error.message
      });
      setIsListening(false);
      setAgentStatus('idle');
    }
  };

  const handleStopConversation = async () => {
    if (!voiceAgentRef.current) return;

    try {
      console.log('🛑 Stopping continuous conversation...');
      
      await voiceAgentRef.current.stopListening();
      
      setIsListening(false);
      setAgentStatus('idle');
      setCurrentTranscript('');
      
      addMessage({
        type: 'system',
        content: '🛑 Konversation beendet'
      });
      
    } catch (error: any) {
      console.error('❌ Error stopping conversation:', error);
      setError(error.message);
      setAgentStatus('idle');
      setIsProcessing(false);
    }
  };

  const handleAgentResponse = (response: any) => {
    console.log('🤖 Agent response received:', response);
    
    setAgentStatus('speaking');
    setIsSpeaking(true);
    setIsProcessing(false);

    addMessage({
      type: 'assistant',
      content: response.text || response.content,
      toolCalls: response.toolCalls?.map((tc: any) => tc.name)
    });

    // Auto-stop speaking after a reasonable time
    // The audio should play during this time
    setTimeout(() => {
      setIsSpeaking(false);
      setAgentStatus('idle');
      console.log('✅ Agent finished speaking');
    }, 5000); // 5 seconds for audio to play
  };

  const addMessage = (message: Omit<Message, 'id' | 'timestamp'>) => {
    setMessages(prev => [...prev, {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: new Date()
    }]);
  };

  const handleClose = () => {
    if (isListening) {
      handleStopConversation();
    }
    onClose();
  };

  // Handle text input submission
  const handleTextSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!textInput.trim() || isProcessing) return;
    
    const userMessage = textInput.trim();
    setTextInput('');
    setIsProcessing(true);
    setAgentStatus('thinking');
    
    // Add user message to chat
    addMessage({
      type: 'user',
      content: userMessage
    });

    try {
      // Use the voice agent's internal processing if available
      if (voiceAgentRef.current) {
        // Directly call the agent's processing method via text
        // @ts-ignore - accessing private method for text input
        await voiceAgentRef.current['processUserInput']?.(userMessage);
      } else {
        // Fallback: Process via MCP directly
        const mcpServer = getMCPServer();
        const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
        
        if (!apiKey) {
          throw new Error('Gemini API Key fehlt');
        }
        
        const genAI = new GoogleGenAI({ apiKey });
        
        // Get available tools
        const toolsResponse = await mcpServer.handleRequest({
          jsonrpc: '2.0',
          id: 'text-tools',
          method: 'tools/list',
          params: {
            _context: {
              orgId: user?.profile?.orgId || user?.uid || '',
              userId: user?.uid || ''
            }
          }
        });
        
        const tools = toolsResponse.result?.tools || [];
        
        // Build detailed tool info for key CRM tools
        const keyTools = [
          'list_tasks', 'create_task', 'update_task',
          'list_leads', 'create_lead', 'get_lead',
          'list_opportunities', 'create_opportunity',
          'generate_briefing', 'search_leads'
        ];
        
        const toolInfo = tools
          .filter((t: any) => keyTools.includes(t.name))
          .map((t: any) => `${t.name}: ${t.description || 'No description'}`)
          .join('\n');

        const prompt = `Du bist ein freundlicher deutscher CRM-Assistent. Du hilfst dem User seine Aufgaben, Leads und Opportunities zu verwalten.

WICHTIGE TOOLS:
${toolInfo}

STATUS-MAPPING (Natürliche Sprache -> Technische Werte):
- "offen", "offene", "ausstehend", "zu erledigen" -> KEIN Filter (zeigt pending + in_progress)
- "in Bearbeitung", "laufend" -> status=in_progress
- "erledigt", "abgeschlossen", "fertig" -> status=completed
- "abgebrochen", "storniert" -> status=cancelled

PRIORITÄTS-MAPPING:
- "dringend", "wichtig", "sofort" -> priority=urgent oder priority=high
- "normal" -> priority=medium
- "niedrig", "unwichtig" -> priority=low

BEISPIELE für Tool-Aufrufe:
- "Wie viele offene Aufgaben?" -> [TOOL:list_tasks] (OHNE status-Filter, dann zähle nicht-completed)
- "Zeig meine Tasks" -> [TOOL:list_tasks]
- "Tasks für heute" -> [TOOL:list_tasks|dueToday=true]
- "Überfällige Aufgaben" -> [TOOL:list_tasks|overdue=true]
- "Erledigte Tasks" -> [TOOL:list_tasks|status=completed]
- "Erstelle Task Anruf bei Max" -> [TOOL:create_task|title=Anruf bei Max]
- "Wie viele Leads habe ich?" -> [TOOL:list_leads]
- "Briefing für Firma X" -> [TOOL:generate_briefing|companyName=Firma X]

REGELN:
1. Antworte auf Deutsch, kurz und freundlich (max 2-3 Sätze)
2. Bei CRM-Anfragen IMMER ein Tool nutzen: [TOOL:name|param1=value1|param2=value2]
3. Bei Zählfragen ("wie viele") das passende list_* Tool aufrufen
4. NIEMALS nach technischen Status-Namen fragen - mappe die natürliche Sprache selbst!
5. Bei "offene Tasks" KEINEN status-Filter setzen, dann zähle alle mit status != completed

User: ${userMessage}
Assistant:`;

        const response = await genAI.models.generateContent({
          model: 'gemini-3-flash-preview',
          contents: prompt,
          config: { 
            temperature: 0.7, 
            maxOutputTokens: 400,
            thinkingConfig: { thinkingLevel: 'low' }
          }
        });

        let responseText = '';
        try {
          // @ts-ignore
          responseText = response.text || '';
        } catch {
          responseText = response.candidates?.[0]?.content?.parts?.[0]?.text || '';
        }

        // Check for tool calls
        const toolMatch = responseText.match(/\[TOOL:([^\]]+)\]/);
        let cleanResponse = responseText.replace(/\[TOOL:[^\]]+\]/g, '').trim();

        if (toolMatch) {
          const toolParts = toolMatch[1].split('|');
          const toolName = toolParts[0];
          const args: any = {};
          
          for (let i = 1; i < toolParts.length; i++) {
            const [key, value] = toolParts[i].split('=');
            if (key && value) args[key] = value;
          }

          addMessage({
            type: 'system',
            content: `🔧 ${toolName}...`
          });

          // Execute tool
          const toolResult = await mcpServer.handleRequest({
            jsonrpc: '2.0',
            id: `tool-${Date.now()}`,
            method: 'tools/call',
            params: {
              name: toolName,
              arguments: args,
              _context: {
                orgId: user?.profile?.orgId || user?.uid || '',
                userId: user?.uid || ''
              }
            }
          });

          // Process result based on tool type
          // MCP returns: { result: { content: [{ type: "text", text: "{ success: true, tasks: [...] }" }] } }
          let parsedResult: any = toolResult.result || {};
          if (parsedResult?.content?.[0]?.text) {
            try {
              parsedResult = JSON.parse(parsedResult.content[0].text);
              console.log('📊 Parsed MCP result:', JSON.stringify(parsedResult).substring(0, 500));
            } catch (e) {
              console.log('⚠️ Could not parse MCP response');
            }
          }
          
          let resultSummary = '';
          
          if (toolName === 'list_tasks' && parsedResult.tasks) {
            const tasks = parsedResult.tasks;
            const openTasks = tasks.filter((t: any) => t.status !== 'completed' && t.status !== 'cancelled');
            const pendingCount = tasks.filter((t: any) => t.status === 'pending').length;
            const inProgressCount = tasks.filter((t: any) => t.status === 'in_progress').length;
            const completedCount = tasks.filter((t: any) => t.status === 'completed').length;
            
            resultSummary = `Gefundene Tasks: ${tasks.length} gesamt, davon ${openTasks.length} offen (${pendingCount} ausstehend, ${inProgressCount} in Bearbeitung), ${completedCount} erledigt.`;
            if (openTasks.length > 0 && openTasks.length <= 5) {
              resultSummary += ` Offene: ${openTasks.map((t: any) => t.title).join(', ')}.`;
            }
          } else if (toolName === 'list_leads' && parsedResult.leads) {
            resultSummary = `Gefundene Leads: ${parsedResult.leads.length}. ${parsedResult.leads.slice(0, 3).map((l: any) => l.name || l.company).join(', ')}${parsedResult.leads.length > 3 ? '...' : ''}`;
          } else if (toolName === 'list_opportunities' && parsedResult.opportunities) {
            resultSummary = `Gefundene Opportunities: ${parsedResult.opportunities.length}.`;
          } else if (parsedResult.success) {
            resultSummary = `Erfolgreich: ${parsedResult.message || JSON.stringify(parsedResult).substring(0, 200)}`;
          } else if (toolResult.error) {
            resultSummary = `Fehler: ${toolResult.error.message || toolResult.error}`;
          } else {
            resultSummary = JSON.stringify(parsedResult).substring(0, 300);
          }

          // Generate user-friendly response based on the result
          console.log('📊 Tool result summary:', resultSummary);
          
          // For counting questions, answer directly without another AI call
          if (userMessage.toLowerCase().includes('wie viele') || userMessage.toLowerCase().includes('wieviele')) {
            cleanResponse = resultSummary;
          } else {
            // Generate natural response for other queries
            const toolResponsePrompt = `Du bist ein deutscher CRM-Assistent. Du hast gerade "${toolName}" ausgeführt.

User fragte: "${userMessage}"
Ergebnis: ${resultSummary}

REGELN:
- Antworte kurz, freundlich und auf Deutsch (1-2 Sätze)
- Beantworte die Frage des Users direkt
- Nenne konkrete Zahlen wenn nach "wie viele" gefragt wurde
- NIEMALS technische IDs nennen
- Bei leeren Ergebnissen freundlich sagen, dass nichts gefunden wurde

Deine Antwort:`;

            const toolResponse = await genAI.models.generateContent({
              model: 'gemini-3-flash-preview',
              contents: toolResponsePrompt,
              config: { 
                temperature: 0.7, 
                maxOutputTokens: 300,
                thinkingConfig: { thinkingLevel: 'low' }
              }
            });

            console.log('📝 Tool response raw:', JSON.stringify(toolResponse, null, 2).substring(0, 800));

            try {
              // @ts-ignore
              cleanResponse = toolResponse.text || '';
            } catch {
              cleanResponse = toolResponse.candidates?.[0]?.content?.parts?.[0]?.text || '';
            }
            
            // Fallback if response is still empty or too short
            if (!cleanResponse || cleanResponse.length < 10) {
              cleanResponse = resultSummary;
            }
          }
          
          console.log('✅ Final response:', cleanResponse);
        }

        addMessage({
          type: 'assistant',
          content: cleanResponse,
          toolCalls: toolMatch ? [toolMatch[1].split('|')[0]] : undefined
        });
      }
    } catch (error: any) {
      console.error('Text processing error:', error);
      addMessage({
        type: 'system',
        content: `❌ Fehler: ${error.message}`
      });
    } finally {
      setIsProcessing(false);
      setAgentStatus('idle');
    }
  };

  // Handle suggested question click
  const handleSuggestedQuestion = (question: string) => {
    setTextInput(question);
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 100);
  };

  // Context-aware suggested questions
  const suggestedQuestions = unifiedServiceReady 
    ? unifiedAgentService.getSuggestedQuestions(currentPage)
    : SUGGESTED_QUESTIONS[currentPage]?.de || [
        'Zeige meine Aufgaben',
        'Erstelle einen Task für morgen',
        'Wie viele Leads habe ich?',
        'Briefing für [Kundenname]',
        'Liste meine Opportunities'
      ];
  
  // Get page-specific title
  const getPageTitle = () => {
    switch (currentPage) {
      case 'tasks': return 'Tasks';
      case 'leads': return 'Leads';
      case 'opportunities': return 'Opportunities';
      case 'google-ads': return 'Google Ads';
      case 'dashboard': return 'Dashboard';
      default: return currentPage;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black bg-accent-soft to-black animate-gradient-slow">
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Header */}
      <div className="relative z-10 p-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-accent/30 backdrop-blur-lg rounded-xl border border-accent/30">
            <Brain className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-txt-inverted">Agent Mode</h1>
            <p className="text-sm text-accent">Voice-powered CRM assistant</p>
          </div>
          {/* Context Badge */}
          {currentPage && currentPage !== 'dashboard' && (
            <span className="ml-3 px-2 py-1 bg-accent/30 text-accent text-xs rounded-full">
              {getPageTitle()}
            </span>
          )}
        </div>

        <button
          onClick={handleClose}
          className="p-3 bg-white/10 hover:bg-bg-card/20 backdrop-blur-lg rounded-xl border border-white/20 transition-colors text-txt-inverted"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="relative z-10 flex flex-col lg:flex-row h-[calc(100vh-100px)] px-4 lg:px-6 gap-4 lg:gap-6">
        
        {/* Left Column - Voice Interface (60%) */}
        <div className="lg:w-[55%] flex flex-col items-center justify-center py-6 lg:py-0">
          <div className="text-center space-y-6">
            
            {/* Voice Visualizer */}
            <VoiceVisualizer
              isActive={isListening || isSpeaking}
              audioLevel={audioLevel}
              status={agentStatus}
            />

            {/* Status Text */}
            <div className="min-h-[50px]">
              {agentStatus === 'idle' && (
                <p className="text-xl lg:text-2xl text-accent animate-pulse">
                  Bereit dir zu helfen
                </p>
              )}
              {agentStatus === 'listening' && (
                <p className="text-xl lg:text-2xl text-success animate-pulse">
                  Ich höre zu...
                </p>
              )}
              {agentStatus === 'thinking' && (
                <p className="text-xl lg:text-2xl text-info animate-pulse">
                  Verarbeite...
                </p>
              )}
              {agentStatus === 'speaking' && (
                <p className="text-xl lg:text-2xl text-accent animate-pulse">
                  Spreche...
                </p>
              )}
            </div>

            {/* Conversation Button */}
            <div className="flex flex-col items-center justify-center gap-3">
              <button
                onClick={isListening ? handleStopConversation : handleStartConversation}
                disabled={isProcessing}
                className={`
                  relative px-6 py-4 lg:px-8 lg:py-5 rounded-2xl transition-all duration-300 transform
                  ${isListening 
                    ? 'bg-danger hover:bg-danger-soft scale-105 shadow-heavy shadow-glow-danger' 
                    : 'bg-gradient-to-r from-accent to-info hover:from-accent hover:to-info hover:scale-105 shadow-card shadow-accent/20'
                  }
                  ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
                  disabled:opacity-50
                  font-semibold text-txt-inverted text-base lg:text-lg
                  flex items-center gap-3
                `}
              >
                {isListening ? (
                  <>
                    <MicOff className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span>Stoppen</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span>Sprechen</span>
                  </>
                )}
                
                {/* Pulsing ring when active */}
                {isListening && (
                  <>
                    <span className="absolute inset-0 rounded-2xl bg-danger animate-ping opacity-75" />
                    <span className="absolute inset-0 rounded-2xl bg-danger animate-pulse opacity-50" />
                  </>
                )}
              </button>
              
              {/* Action Hint */}
              <p className="text-accent text-xs lg:text-sm text-center max-w-sm">
                {isListening 
                  ? '🎙️ Sprich natürlich - ich antworte automatisch' 
                  : '💬 Oder nutze den Chat rechts zum Tippen'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="max-w-sm mx-auto p-3 bg-danger/20 backdrop-blur-lg rounded-xl border border-danger/30">
                <div className="flex items-center space-x-2 text-danger">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-xs lg:text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="flex items-center justify-center gap-3 pt-2">
              {messages.filter(m => m.type === 'assistant').length > 0 && (
                <div className="px-3 py-1.5 bg-success/20 backdrop-blur-lg rounded-lg border border-success/30">
                  <div className="flex items-center space-x-1.5 text-success">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">
                      {messages.filter(m => m.type === 'assistant').length} Antworten
                    </span>
                  </div>
                </div>
              )}
              
              {messages.some(m => m.toolCalls) && (
                <div className="px-3 py-1.5 bg-info/20 backdrop-blur-lg rounded-lg border border-accent/30">
                  <div className="flex items-center space-x-1.5 text-info">
                    <Zap className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">
                      {messages.filter(m => m.toolCalls).length} Aktionen
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Chat Panel (45%) */}
        <div className="lg:w-[45%] flex flex-col h-full max-h-[50vh] lg:max-h-full">
          <div className="flex-1 flex flex-col bg-black/60 backdrop-blur-xl rounded-2xl border border-white/20 overflow-hidden shadow-heavy">
            
            {/* Chat Header */}
            <div className="px-4 py-3 border-b border-white/10 bg-white/5 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4 text-accent" />
                <span className="text-sm font-medium text-txt-inverted">Chat</span>
                {(messages.length > 0 || currentTranscript) && (
                  <div className="w-2 h-2 bg-success/80 rounded-full animate-pulse" />
                )}
              </div>
              <span className="text-xs text-txt-muted">
                {messages.length} Nachrichten
              </span>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-accent scrollbar-track-transparent">
              {messages.length === 0 && !currentTranscript && (
                <div className="text-center py-8 text-txt-muted">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Starte eine Konversation per Voice oder Text</p>
                </div>
              )}
              
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-2 ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.type !== 'user' && (
                    <div className={`flex-shrink-0 p-1.5 rounded-lg ${
                      message.type === 'assistant' 
                        ? 'bg-accent/30' 
                        : 'bg-accent/30'
                    }`}>
                      {message.type === 'assistant' ? (
                        <Bot className="w-3.5 h-3.5 text-accent" />
                      ) : (
                        <Info className="w-3.5 h-3.5 text-info" />
                      )}
                    </div>
                  )}

                  <div className={`flex-1 max-w-[85%] ${
                    message.type === 'user' ? 'text-right' : 'text-left'
                  }`}>
                    <div className={`inline-block px-3 py-2 rounded-xl ${
                      message.type === 'user'
                        ? 'bg-success-soft/30 text-success'
                        : message.type === 'assistant'
                        ? 'bg-accent/30 text-accent'
                        : 'bg-accent/30 text-info'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      
                      {/* Tool calls indicator */}
                      {message.toolCalls && message.toolCalls.length > 0 && (
                        <div className="mt-1.5 flex items-center space-x-1 text-xs opacity-75">
                          <Zap className="w-3 h-3" />
                          <span>{message.toolCalls.join(', ')}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-0.5 px-1 text-[10px] text-txt-tertiary">
                      {message.timestamp.toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>

                  {message.type === 'user' && (
                    <div className="flex-shrink-0 p-1.5 bg-success-soft/30 rounded-lg">
                      <User className="w-3.5 h-3.5 text-success" />
                    </div>
                  )}
                </div>
              ))}

              {/* Current transcript (while speaking) */}
              {currentTranscript && (
                <div className="flex items-start space-x-2 justify-end">
                  <div className="flex-1 max-w-[85%] text-right">
                    <div className="inline-block px-3 py-2 rounded-xl bg-success-soft/20 text-success border border-success/30">
                      <p className="text-sm leading-relaxed animate-pulse">
                        {currentTranscript}
                      </p>
                    </div>
                    <div className="mt-0.5 px-1 text-[10px] text-txt-tertiary">
                      Spricht...
                    </div>
                  </div>
                  <div className="flex-shrink-0 p-1.5 bg-success-soft/30 rounded-lg animate-pulse">
                    <User className="w-3.5 h-3.5 text-success" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Suggested Questions */}
            {messages.length <= 3 && (
              <div className="px-3 py-2 border-t border-white/5 bg-white/5">
                <div className="flex flex-wrap gap-1.5">
                  {suggestedQuestions.slice(0, 4).map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="px-2.5 py-1 bg-accent/20 hover:bg-accent/40 backdrop-blur-lg rounded-full border border-accent/30 text-accent text-[11px] transition-colors truncate max-w-[150px]"
                      title={question}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Text Input */}
            <div className="p-3 border-t border-white/10 bg-white/5">
              <form onSubmit={handleTextSubmit} className="flex gap-2">
                <input
                  ref={textInputRef}
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Nachricht eingeben..."
                  disabled={isProcessing}
                  className="flex-1 px-3 py-2 bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl text-txt-inverted text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-accent/30/50 focus:border-accent/50 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={isProcessing || !textInput.trim()}
                  className="px-3 py-2 bg-gradient-to-r from-accent to-info hover:from-accent hover:to-info rounded-xl text-txt-inverted transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

